import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Image,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Spin,
  Tag,
  Upload
} from 'antd';
import {useAuth} from '../contexts/AuthContext';
import {DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, TeamOutlined} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import ResizableTable from '../components/ResizableTable';
import CustomerSelector from '../components/CustomerSelector';

const { Option } = Select;

const TeacherList = ({ user }) => {
  const [teachers, setTeachers] = useState([]);

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [teamModalVisible, setTeamModalVisible] = useState(false);
  const [teams, setTeams] = useState([]);
  const [editingTeam, setEditingTeam] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [customerDisabled, setCustomerDisabled] = useState(false);
  const [teamForm] = Form.useForm();
  const [teamTeachers, setTeamTeachers] = useState([]);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editAvatar, setEditAvatar] = useState(''); // 存储原有头像
  const [avatarFile, setAvatarFile] = useState(null); // 存储头像文件
  const [switchCustomerModalVisible, setSwitchCustomerModalVisible] = useState(false);
  const [switchingTeacher, setSwitchingTeacher] = useState(null);
  const [switchForm] = Form.useForm();
  const [userRole, setUserRole] = useState('');
  const { apiClient } = useAuth();
  const quillRef = useRef();
  
  // 用于存储当前编辑的教练记录，以便在classes加载完成后设置classId
  const editingRecordRef = useRef(null);

  // 星级和师傅相关状态
  const [starLevels, setStarLevels] = useState([]);
  const [masters, setMasters] = useState([]);

  // ReactQuill modules and formats
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: (value) => {
          if (value) {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();
            input.onchange = async () => {
              const file = input.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  const quill = quillRef.current?.getEditor();
                  if (quill) {
                    const range = quill.getSelection();
                    const index = range ? range.index : quill.getLength();
                    quill.insertEmbed(index, 'image', e.target.result);
                    // After inserting, find the image and set style
                    setTimeout(() => {
                      const editor = quill.root;
                      const images = editor.querySelectorAll('img');
                      images.forEach(img => {
                        img.style.maxWidth = '100%';
                        img.style.height = 'auto';
                      });
                    }, 0);
                  }
                };
                reader.readAsDataURL(file);
              }
            };
          }
        }
      }
    }
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent', 'link', 'image'
  ];


  
  // 当API客户端可用时也获取星级数据
  useEffect(() => {
    if (apiClient) {
      fetchStarLevels();
    }
  }, [apiClient]);

  // Role-based settings
  useEffect(() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const user = JSON.parse(rawUser);
        setSelectedCustomerId(user.customerId);
        setUserRole(user.role || '');
        if (user.role !== 'R00001') {
          setCustomerDisabled(true);
        } else {
          setCustomerDisabled(false);
        }
      }
    } catch (err) {
      console.error('读取用户信息失败:', err);
    }
  }, []);

  useEffect(() => {
    fetchClassList();
    fetchMasters(); // 当校区改变时重新获取师傅列表
  }, [selectedCustomerId]);

  useEffect(() => {
    fetchTeachers();
  }, [selectedCustomerId]);
  
  // 当classes加载完成且正在编辑时，重新设置classId
  useEffect(() => {
    console.log('useEffect触发 - classes长度:', classes.length, 'isCreating:', isCreating, 'editModalVisible:', editModalVisible, 'editingRecordRef:', editingRecordRef.current ? '有值' : '无值');
    if (!isCreating && editModalVisible && editingRecordRef.current && classes.length > 0) {
      const record = editingRecordRef.current;
      console.log('useEffect中处理record:', record.classId, record.className);
      // 延迟设置，确保Select组件已经渲染且有选项
      setTimeout(() => {
        // 如果record中有classId，直接设置（确保下拉框能正确显示）
        if (record.classId) {
          editForm.setFieldsValue({ classId: record.classId });
          console.log('从record中设置classId:', record.classId);
          // 强制更新表单，确保Select显示正确
          const currentValues = editForm.getFieldsValue();
          console.log('设置后表单值:', currentValues);
        } else if (record.className) {
          // 如果record中没有classId但有className，尝试从classes中查找
          const matchedClass = classes.find(cls => cls.className === record.className);
          if (matchedClass) {
            editForm.setFieldsValue({ classId: matchedClass.classId });
            console.log('从classes中匹配到classId:', matchedClass.classId);
          }
        }
      }, 200);
    }
  }, [classes, isCreating, editModalVisible, editForm]);



  const fetchClassList = async () => {
      if (!selectedCustomerId) {
        setClasses([]);
        return;
      }
      // 先尝试从缓存读取
      try {
        const cached = localStorage.getItem(`classList_${selectedCustomerId}`);
        if (cached) {
          setClasses(JSON.parse(cached));
          return;
        }
      } catch (err) {
        console.error('读取小队缓存失败:', err);
      }
      // 缓存没有，调用API
      try {
        const customerId = selectedCustomerId || JSON.parse(localStorage.getItem('user') || '{}').customerId;
        const response = await apiClient.post(`/sports-server-prod/api.v1/customer/queryClassList?customerId=${customerId}`);
        if (response.data.code === 200) {
          const list = response.data.data.list || [];
          setClasses(list);
          localStorage.setItem(`classList_${selectedCustomerId}`, JSON.stringify(list));
        } else {
          message.error('获取小队列表失败');
        }
      } catch (error) {
        console.error('获取小队列表错误:', error);
        message.error('获取小队列表失败');
      }
    };

  const fetchTeachers = async (customerIdParam) => {
    if (!apiClient) {
      console.error('API客户端未初始化');
      message.error('系统初始化中，请稍后重试');
      return;
    }

    setLoading(true);
    
    try {
      const customerId = customerIdParam || selectedCustomerId || JSON.parse(localStorage.getItem('user') || '{}').customerId;
      const response = await apiClient.get('/sports-base-server-prod/main/getTeacherList', {
        params: {
          customerId: customerId,
          pageNum: 1,
          pageSize: 999
        }
      });
      
      if (response.data.result === "1") {
        setTeachers(response.data.teacherList);
      } else {
        message.error('获取教练列表失败');
      }
    } catch (error) {
      console.error('获取教练列表错误:', error);
      message.error('获取教练列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    if (!apiClient) return;
    try {
      const response = await apiClient.get('/sports-base-server-prod/myPage/searchClass', {
        params: { customerId: selectedCustomerId }
      });
      if (response.data.result === '1') {
        setTeams(response.data.classInfoList || []);
      }
    } catch (error) {
      console.error('获取团队列表错误:', error);
    }
  };

  const fetchStarLevels = async () => {
    if (!apiClient) return;
    
    // 先尝试从localStorage获取缓存数据
    try {
      const cachedData = localStorage.getItem('STAR_LEVEL');
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        // 验证数据格式
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setStarLevels(parsedData);
          
          // 如果是创建模式且下拉框已打开，尝试设置默认值
          if (isCreating && editModalVisible && parsedData.length > 0) {
            editForm.setFieldsValue({
              starLevel: parsedData[0].enumCode
            });
          }
          return; // 使用缓存数据，不再调用接口
        }
      }
    } catch (err) {
      console.error('读取星级缓存失败:', err);
    }
    
    // 缓存没有或无效，调用API获取数据
    try {
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'STAR_LEVEL',
        colName: 'STAR_LEVEL',
      });
      
      if (resp.data && resp.data.code === 200) {
        let rawData = resp.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        
        // 根据Course.js中的处理方式构建选项，同时保留remark字段
        const levels = (rawData || []).map((item) => ({
          enumCode: item.enumCode || item.enumName,
          enumLabel: item.enumName,
          remark: item.remark || '', // 添加remark字段
        }));
        
        setStarLevels(levels);
        
        // 缓存到localStorage
        localStorage.setItem('STAR_LEVEL', JSON.stringify(levels));
        
        // 如果是创建模式且下拉框已打开，尝试设置默认值
        if (isCreating && editModalVisible && levels.length > 0) {
          editForm.setFieldsValue({
            starLevel: levels[0].enumCode
          });
        }
      } else {
        console.error('获取星级列表失败:', resp.data?.msg || '未知错误');
        setStarLevels([]); // 确保设置为空数组
      }
    } catch (error) {
      console.error('获取星级列表异常:', error);
      setStarLevels([]); // 错误时也确保设置为空数组
    }
  };
  
  // 添加useEffect来处理starLevels更新后的默认选择
  useEffect(() => {
    if (isCreating && editModalVisible && starLevels.length > 0) {
      editForm.setFieldsValue({
        starLevel: starLevels[0].enumCode
      });
    }
  }, [starLevels, isCreating, editModalVisible]);

  const fetchTeamTeachers = async () => {
    if (!apiClient) return;
    try {
      const customerId = selectedCustomerId || JSON.parse(localStorage.getItem('user') || '{}').customerId;
      const response = await apiClient.get('/sports-base-server-prod/main/getTeacherList', {
        params: { customerId: customerId, pageNum: 1, pageSize: 999 }
      });
      if (response.data.result === '1') {
        setTeamTeachers(response.data.teacherList);
      }
    } catch (error) {
      console.error('获取教练列表错误:', error);
    }
  };

  const fetchMasters = async () => {
    if (!apiClient) return;
    try {
      const customerId = selectedCustomerId || JSON.parse(localStorage.getItem('user') || '{}').customerId;
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await apiClient.get('/sports-base-server-prod/main/getTeacherList', {
        params: { customerId: customerId, pageNum: 1, pageSize: 999 }
      });
      if (response.data.result === '1') {
        // 确保teacherList存在且为数组
        const teacherList = Array.isArray(response.data.teacherList) ? response.data.teacherList : [];
        // 过滤掉当前用户
        setMasters(teacherList);
      } else {
        console.error('获取师傅列表失败:', response.data.msg);
        setMasters([]); // 确保设置为空数组
      }
    } catch (error) {
      console.error('获取师傅列表错误:', error);
      setMasters([]); // 错误时也确保设置为空数组
    }
  };

  const handleCustomerChange = (value) => {
    setSelectedCustomerId(value);
  };

  const handleEdit = (record) => {
    // 保存当前编辑的记录到ref，以便后续使用
    editingRecordRef.current = record;
    
    console.log('编辑教练记录:', JSON.stringify(record, null, 2));
    console.log('当前classes:', classes);
    console.log('record中的classId:', record.classId);
    console.log('record中的className:', record.className);
    
    // 转换 authId 值以匹配单选框选项
    let authIdValue = record.authId;
    if (authIdValue !== '9999') {
      authIdValue = authIdValue === '9002' ? '9002' : '9001';
    }
    
    // 根据 className 从 classes 数组中找到对应的 classId
    let classIdValue = record.classId;
    if (!classIdValue && record.className) {
      const matchedClass = classes.find(cls => cls.className === record.className);
      if (matchedClass) {
        classIdValue = matchedClass.classId;
        console.log('匹配到classId:', classIdValue);
      } else {
        console.log('未找到匹配的小队, className:', record.className);
      }
    }
    
    editForm.setFieldsValue({
      userId: record.userId,
      userName: record.userName,
      phoneDecimal: record.phoneDecimal,
      teacherProfile: record.teacherProfile,
      classId: classIdValue,
      authId: authIdValue,
      starLevel: record.starLevel,
      master: record.master,
    });
    setEditAvatar(record.photoAddress || ''); // 设置原有头像
    setAvatarFile(null); // 清空头像文件
    setIsCreating(false);
    setEditModalVisible(true);
  }

  const handleAdd = () => {
    editForm.resetFields();
    setEditAvatar(''); // 清空头像
    setAvatarFile(null); // 清空头像文件
    setIsCreating(true);
    
    // 在创建模式下，默认选中第一个星级选项
    if (starLevels.length > 0) {
      editForm.setFieldsValue({
        starLevel: starLevels[0].enumCode
      });
    }
    
    setEditModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = {
        userId: isCreating ? '' : values.userId,
        userName: values.userName,
        phoneDecimal: values.phoneDecimal,
        teacherProfile: values.teacherProfile,
        classId: values.classId || '',
        customerId: selectedCustomerId,
        authId: values.authId === '9999' ? '9999' : (values.authId === '9002' ? '9002' : '9001'),
        starLevel: values.starLevel,
        master: values.master,
      };

      // 如果有上传的头像文件，添加fileName和uploadImage参数
      if (avatarFile) {
        payload.fileName = avatarFile.name;
        // 转换为base64
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(avatarFile);
          reader.onload = () => {
            const result = reader.result.split(',')[1]; // 只取base64部分
            resolve(result);
          };
        });
        payload.uploadImage = base64;
      }

      const response = await apiClient.post('/sports-base-server-prod/main/updateTeacher', payload);
      if (response.data.result === '1') {
        message.success(isCreating ? '新增成功' : '修改成功');
        setEditModalVisible(false);
        setAvatarFile(null); // 清空头像文件
        editingRecordRef.current = null; // 清空编辑记录
        
        // 删除当前校区的教练缓存数据
        const customerId = selectedCustomerId || JSON.parse(localStorage.getItem('user') || '{}').customerId;
        if (customerId) {
          localStorage.removeItem(`teacher_${customerId}`);
          console.log(`已删除教练缓存: teacher_${customerId}`);
        }
        
        fetchTeachers();
      } else {
        message.error(isCreating ? '新增失败' : '修改失败');
      }
    } catch (error) {
      console.error('保存错误:', error);
      message.error('保存失败，请稍后重试');
    }
  };

    const handleDelete = async (record) => {
    try {
      const response = await apiClient.get('/sports-base-server-prod/main/deleteTeacher', {
        params: { customerId: record.customerId, userId: record.userId }
      });
      if (response.data.result === '1') {
        message.success('删除成功');
        fetchTeachers(record.customerId);
      } else {
        message.error('删除失败');
      }
    } catch (error) {
      console.error('删除错误:', error);
      message.error('删除失败，请稍后重试');
    }
  };

  const handleTeamEdit = (team) => {
    setEditingTeam(team);
    setNewTeamName(team.className);
    setTeamModalVisible(true);
  };

  const handleTeamDelete = async (teamId) => {
    try {
      const response = await apiClient.get('/sports-base-server-prod/myPage/deleteClass', {
        params: { customerId: selectedCustomerId, classId: teamId }
      });
      if (response.data.result === '1') {
        message.success('删除成功');
        fetchTeams();
        fetchClassList();
      } else {
        message.error('删除失败');
      }
    } catch (error) {
      console.error('删除团队错误:', error);
      message.error('删除失败');
    }
  };

  const handleTeamSave = async () => {
    try {
      const values = await teamForm.validateFields();
      const teacher = teamTeachers.find(t => t.userName === values.teacherInCharge);
      const payload = {
        classId: editingTeam ? editingTeam.classId : '',
        className: values.className,
        classType: '04',
        customerId: selectedCustomerId,
        endDate: '2028-12-20',
        endTime: '11:00',
        startDate: dayjs().format('YYYY-MM-DD'),
        startTime: '10:00',
        teacherInCharge: values.teacherInCharge,
        userId: teacher?.userId || '',
      };

      const response = await apiClient.post('/sports-base-server-prod/myPage/updateClass', payload);
      if (response.data.result === '1') {
        message.success('保存成功');
        setIsCreatingTeam(false);
        setEditingTeam(null);
        teamForm.resetFields();
        fetchTeams();
        fetchClassList();
      } else {
        message.error('保存失败');
      }
    } catch (error) {
      console.error('保存团队错误:', error);
      message.error('保存失败');
    }
  };

  const handleSwitchCustomer = (record) => {
    setSwitchingTeacher(record);
    switchForm.resetFields();
    setSwitchCustomerModalVisible(true);
  };

  // 从localStorage获取校区名称
  const getCustomerNameById = (customerId) => {
    try {
      const customerList = localStorage.getItem('customerList');
      if (customerList) {
        const customers = JSON.parse(customerList);
        const customer = customers.find(c => c.customerId === customerId);
        return customer ? customer.customerName : '';
      }
    } catch (error) {
      console.error('获取校区名称失败:', error);
    }
    return '';
  };

  const handleSwitchCustomerSave = async () => {
    try {
      const values = await switchForm.validateFields();
      const newCustomerId = values.newCustomerId;
      
      // 检查是否与当前校区相同
      if (newCustomerId === selectedCustomerId) {
        message.warning('新校区不能与当前校区相同');
        return;
      }

      const payload = {
        oldCustomerId: selectedCustomerId,
        oldUserId: switchingTeacher.userId,
        newCustomerId: newCustomerId
      };

      const response = await apiClient.post('/sports-server-prod/api.v1/customer/switchUserCustomer', payload);
      if (response.data.code === 200) {
        message.success('校区切换成功');
        setSwitchCustomerModalVisible(false);
        setSwitchingTeacher(null);
        switchForm.resetFields();
        
        // 删除原校区和新校区的教练缓存数据
        const oldCustomerId = selectedCustomerId;
        const newCustomerId = newCustomerId;
        
        if (oldCustomerId) {
          localStorage.removeItem(`teacher_${oldCustomerId}`);
          console.log(`已删除原校区教练缓存: teacher_${oldCustomerId}`);
        }
        if (newCustomerId) {
          localStorage.removeItem(`teacher_${newCustomerId}`);
          console.log(`已删除新校区教练缓存: teacher_${newCustomerId}`);
        }
        
        fetchTeachers();
      } else {
        message.error(response.data.msg || '校区切换失败');
      }
    } catch (error) {
      console.error('校区切换错误:', error);
      message.error('校区切换失败，请稍后重试');
    }
  };

  // 添加列状态管理
  const [columns, setColumns] = useState([]);
  
  // 使用 useMemo 动态生成列配置
  const baseColumns = useMemo(() => {
    const columns = [
      {
        title: '头像',
        dataIndex: 'photoAddress',
        key: 'photoAddress',
        width: 100,
        render: (photoAddress) => (
          <Image
            src={photoAddress}
            alt="教练头像"
            style={{ width: 70, height: 70, borderRadius: '50%' }}
            fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvbnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPlRlYWNoZXI8L3RleHQ+PC9zdmc+"
          />
        ),
      },
      {
        title: '用户名',
        dataIndex: 'userName',
        key: 'userName',
        width: 150,
        sorter: (a, b) => {
          const nameA = a.userName || '';
          const nameB = b.userName || '';
          return nameA.localeCompare(nameB);
        },
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="搜索用户名"
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              搜索
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              重置
            </Button>
          </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => {
          const name = record.userName || '';
          return name.toLowerCase().includes(value.toLowerCase());
        },
      },
      {
        title: '联系方式',
        dataIndex: 'phoneDecimal',
        key: 'phoneDecimal',
        width: 150,
        sorter: (a, b) => {
          const phoneA = a.phoneDecimal || '';
          const phoneB = b.phoneDecimal || '';
          return phoneA.localeCompare(phoneB);
        },
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="搜索联系方式"
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              搜索
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              重置
            </Button>
          </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => {
          const phone = record.phoneDecimal || '';
          return phone.toLowerCase().includes(value.toLowerCase());
        },
      },
      {
        title: '小队',
        dataIndex: 'className',
        key: 'className',
        width: 150,
        sorter: (a, b) => {
          const classA = a.className || '';
          const classB = b.className || '';
          return classA.localeCompare(classB);
        },
        render: (text) => text || '-'
      }
    ];
    
    // 如果用户角色是R00001，添加星级列
    if (userRole === 'R00001') {
      columns.push({
        title: '星级',
        dataIndex: 'starLevel',
        key: 'starLevel',
        width: 100,
        render: (text) => text || '-'
      });
    }
    
    // 添加操作列
    columns.push({
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: userRole === 'R00001' ? 250 : 150,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {userRole === 'R00001' && (
              <Button
                  type="default"
                  size="small"
                  onClick={() => handleSwitchCustomer(record)}
              >
                校区变更
              </Button>
          )}
          <Popconfirm
            title="确认删除"
            description={`确定要删除教练"${record.userName}"吗？`}
            onConfirm={() => handleDelete(record)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              size="small"
              icon={<DeleteOutlined />}
              danger
            >
              删除
            </Button>
          </Popconfirm>

        </div>
      ),
    });
    
    return columns;
  }, [userRole]);
  
  // 当 baseColumns 变化时更新 columns
  useEffect(() => {
    setColumns(baseColumns);
  }, [baseColumns]);

  const teamColumns = [
    {
      title: '小队名称',
      dataIndex: 'teamName',
      key: 'teamName',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleTeamEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description={`确定要删除团队"${record.teamName}"吗？`}
            onConfirm={() => handleTeamDelete(record.teamId)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              size="small"
              icon={<DeleteOutlined />}
              danger
            >
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    }
  ];

  return (
    <div className="menu-item-content">
      <Card title={`教练列表 (共 ${teachers.length} 条)`} style={{ margin: '16px' }}>
        <Form layout="inline" style={{ marginBottom: '16px' }}>
          <Form.Item label="校区选择">
            <CustomerSelector
              value={selectedCustomerId}
              onChange={handleCustomerChange}
              disabled={customerDisabled}
              showTeamSelector={false}
              style={{ width: 350 }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={() => {
              const customerId = selectedCustomerId || JSON.parse(localStorage.getItem('user') || '{}').customerId;
              fetchTeachers();
            }}>
              <SearchOutlined /> 搜索
            </Button>
          </Form.Item>
        </Form>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          style={{ marginBottom: '16px' }}
        >
          新增
        </Button>
        <Spin spinning={loading}>
          <ResizableTable
            dataSource={teachers}
            columns={columns}
            onColumnsChange={setColumns}
            rowKey="userId"
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
          />
        </Spin>
      </Card>

      <Modal
        title={isCreating ? "新增教练" : "编辑教练"}
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); setIsCreating(false); editingRecordRef.current = null; }}
        onOk={handleSave}
        width={800}
      >
        {/* Avatar upload */}
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          {editAvatar || avatarFile ? (
            <div>
              <Image 
                src={avatarFile ? URL.createObjectURL(avatarFile) : editAvatar} 
                width={96} 
                height={96} 
                style={{ borderRadius: 8 }} 
              />
              <div style={{ marginTop: 8 }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    // 验证文件类型
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('只能上传图片文件！');
                      return false;
                    }
                    // 验证文件大小（小于5MB）
                    const isLt5M = file.size / 1024 / 1024 < 5;
                    if (!isLt5M) {
                      message.error('图片大小不能超过5MB！');
                      return false;
                    }
                    setAvatarFile(file);
                    setEditAvatar(''); // 清空原有头像
                    return false; // 阻止自动上传
                  }}
                >
                  <Button size="small">更换头像</Button>
                </Upload>
              </div>
            </div>
          ) : (
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('只能上传图片文件！');
                  return false;
                }
                const isLt5M = file.size / 1024 / 1024 < 5;
                if (!isLt5M) {
                  message.error('图片大小不能超过5MB！');
                  return false;
                }
                setAvatarFile(file);
                return false;
              }}
            >
              <div style={{ 
                width: 96, 
                height: 96, 
                border: '1px dashed #d9d9d9', 
                borderRadius: 8, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                cursor: 'pointer',
                margin: '0 auto'
              }}>
                <div>
                  <PlusOutlined style={{ fontSize: 24, color: '#999' }} />
                  <div style={{ marginTop: 8, color: '#999' }}>上传头像</div>
                </div>
              </div>
            </Upload>
          )}
        </div>
        <Form
          form={editForm}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              {!isCreating && (
                <Form.Item label="用户ID" name="userId">
                  <Input disabled />
                </Form.Item>
              )}
              <Form.Item
                label="用户名"
                name="userName"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input />
              </Form.Item>
              {editForm.getFieldValue('authId') !== '9999' && (
                <Form.Item
                  label="是否管理员"
                  name="authId"
                  rules={[{ required: true, message: '请选择是否管理员' }]}
                >
                  <Radio.Group>
                    <Radio value="9002">是</Radio>
                    <Radio value="9001">否</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
              <Form.Item
                label="联系方式"
                name="phoneDecimal"
                rules={[{ required: true, message: '请输入联系方式' }]}
              >
                <Input />
              </Form.Item>
              {(userRole === 'R00001') && (
              <Form.Item label="星级" name="starLevel">
                <Select placeholder="请选择星级">
                  {Array.isArray(starLevels) && starLevels.map(level => (
                    <Option key={level.enumCode} value={level.enumCode}>{level.enumLabel}</Option>
                  ))}
                </Select>
              </Form.Item>
              )}
              <Form.Item label="导师" name="master">
                <Select placeholder="请选择师傅">
                  {Array.isArray(masters) && masters.map(master => (
                    <Option key={master.userId} value={master.userId}>{master.userName}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="所属小队" name="classId">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Select 
                    placeholder="请选择" 
                    style={{ flex: 1 }}
                    value={editForm.getFieldValue('classId')}
                    onChange={(value) => editForm.setFieldsValue({ classId: value })}
                  >
                    {classes.map(cls => (
                      <Option key={cls.classId} value={cls.classId}>
                        {cls.className}
                      </Option>
                    ))}
                  </Select>
                  <Button
                    type="link"
                    onClick={() => { fetchTeams(); setTeamModalVisible(true); }}
                    style={{ marginLeft: 8 }}
                  >
                    <TeamOutlined /> 维护小队
                  </Button>
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    简介
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => setIsEditingProfile(!isEditingProfile)}
                      style={{ marginLeft: 8, padding: 0 }}
                    >
                    </Button>
                  </div>
                }
                name="teacherProfile"
              >
                {isEditingProfile ? (
                  <ReactQuill
                    theme="snow"
                    style={{ height: '300px' }}
                    modules={quillModules}
                    formats={quillFormats}
                    ref={quillRef}
                  />
                ) : (
                  <div
                    style={{
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      padding: '8px',
                      minHeight: '300px',
                      backgroundColor: '#fafafa'
                    }}
                    dangerouslySetInnerHTML={{ __html: editForm.getFieldValue('teacherProfile') || '暂无简介' }}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="校区变更"
        open={switchCustomerModalVisible}
        onCancel={() => {
          setSwitchCustomerModalVisible(false);
          setSwitchingTeacher(null);
          switchForm.resetFields();
        }}
        onOk={handleSwitchCustomerSave}
        width={500}
      >
        <Form
          form={switchForm}
          layout="vertical"
        >
          <Form.Item label="教练信息">
            <Input value={switchingTeacher?.userName} disabled />
          </Form.Item>
          <Form.Item label="当前校区">
            <Input value={getCustomerNameById(selectedCustomerId)} disabled />
          </Form.Item>
          <Form.Item
            label="目标校区"
            name="newCustomerId"
            rules={[{ required: true, message: '请选择目标校区' }]}
          >
            <CustomerSelector
              showTeamSelector={false}
              placeholder="请选择目标校区"
              onCustomerChange={(value) => {
                switchForm.setFieldsValue({ newCustomerId: value });
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="维护小队"
        open={teamModalVisible}
        onCancel={() => setTeamModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => { setIsCreatingTeam(true); setEditingTeam(null); teamForm.resetFields(); fetchTeamTeachers(); }}
          >
            新增小队
          </Button>
        </div>
        <div>
          {teams.map(team => (
            <Tag
              key={team.classId}
              style={{ marginBottom: 8, padding: '4px 8px' }}
            >
              {team.className}
              <Button
                type="link"
                size="small"
                onClick={() => { setIsCreatingTeam(true); setEditingTeam(team); teamForm.setFieldsValue({ className: team.className, teacherInCharge: team.teacherInCharge }); fetchTeamTeachers(); }}
                style={{ marginLeft: 8 }}
              >
                编辑
              </Button>
              <Popconfirm
                title="确认删除"
                description={`确定要删除团队"${team.className}"吗？`}
                onConfirm={() => handleTeamDelete(team.classId)}
                okText="确认"
                cancelText="取消"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  style={{ marginLeft: 8 }}
                >
                  删除
                </Button>
              </Popconfirm>
            </Tag>
          ))}
        </div>
        <Modal
          title={editingTeam ? "编辑小队" : "新增小队"}
          open={isCreatingTeam}
          onCancel={() => { setIsCreatingTeam(false); setEditingTeam(null); teamForm.resetFields(); }}
          onOk={handleTeamSave}
          width={400}
        >
          <Form
            form={teamForm}
            layout="vertical"
          >
            <Form.Item
              label="小队名称"
              name="className"
              rules={[{ required: true, message: '请输入小队名称' }]}
            >
              <Input placeholder="请输入小队名称" />
            </Form.Item>
            <Form.Item
              label="主教练"
              name="teacherInCharge"
              rules={[{ required: true, message: '请选择主教练' }]}
            >
              <Select placeholder="请选择主教练">
                {teamTeachers.map(t => (
                  <Option key={t.userId} value={t.userName}>{t.userName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Modal>
    </div>
  );
};

export default TeacherList;

